import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Youtube, Twitter } from "lucide-react";
import useCampuses from "../hooks/useCampuses";

const Footer = () => {
  const campusList = useCampuses();
  const mainCampus = campusList.find((c) => c.featured) || campusList[0] || {};

  const quickLinks = [
    { label: "Home", path: "/" },
    { label: "About Us", path: "/about" },
    { label: "Academics", path: "/academics" },
    { label: "Admissions", path: "/admissions" },
    { label: "Gallery", path: "/gallery" },
    { label: "Contact", path: "/contact" }
  ];

  return (
    <footer className="bg-[#1A0505] text-gray-300">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* About column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#B41C2C] rounded-lg flex items-center justify-center">
                <span className="text-white font-extrabold text-lg">T</span>
              </div>
              <div>
                <div className="text-white font-bold text-sm leading-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  The Indian Public School
                </div>
                <div className="text-[#C83C4A] text-xs font-semibold tracking-widest uppercase">Bengaluru</div>
              </div>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed mb-5">
              Situated in the northern part of the city, TIPS Bengaluru caters to the crucial learning needs of students. Education at TIPS bridges the gap between dreams and realities.
            </p>
            <div className="text-xs text-gray-500">
              <span className="text-gray-400 font-medium">CBSE Affiliation No:</span> 830884
            </div>
            <div className="flex items-center gap-3 mt-5">
              <a href="#" aria-label="Facebook" className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center hover:bg-[#B41C2C] transition-colors">
                <Facebook size={14} className="text-gray-300" />
              </a>
              <a href="#" aria-label="Instagram" className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center hover:bg-[#B41C2C] transition-colors">
                <Instagram size={14} className="text-gray-300" />
              </a>
              <a href="#" aria-label="YouTube" className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center hover:bg-[#B41C2C] transition-colors">
                <Youtube size={14} className="text-gray-300" />
              </a>
              <a href="#" aria-label="Twitter" className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center hover:bg-[#B41C2C] transition-colors">
                <Twitter size={14} className="text-gray-300" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">Quick Links</h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 hover:text-[#C83C4A] transition-colors flex items-center gap-2"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#B41C2C] flex-shrink-0"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Campuses */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">Our Campuses</h4>
            <ul className="space-y-2.5">
              {campusList.map((campus) => (
                <li key={campus.id}>
                  <Link
                    to={`/campuses#${campus.id}`}
                    className="text-sm text-gray-400 hover:text-[#C83C4A] transition-colors flex items-center gap-2"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#B41C2C] flex-shrink-0"></span>
                    {campus.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">Contact Info</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-[#C83C4A] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-400 leading-relaxed">
                  {mainCampus.address || "# 78/4/1, Vidya Nidhi, Opp. Manyata Tech Park Gate-1, HBR Layout, Nagawara, Bengaluru – 560045"}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-[#C83C4A] flex-shrink-0" />
                <div className="text-sm">
                  <a href={`tel:${mainCampus.phone || "7619305034"}`} className="text-gray-400 hover:text-[#C83C4A] transition-colors block">{mainCampus.phone || "7619305034"} (Admissions)</a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-[#C83C4A] flex-shrink-0" />
                <a href="mailto:infobengaluru@tipsmail.net" className="text-sm text-gray-400 hover:text-[#C83C4A] transition-colors break-all">
                  infobengaluru@tipsmail.net
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock size={16} className="text-[#C83C4A] flex-shrink-0" />
                <span className="text-sm text-gray-400">Mon – Sat, 08:00 – 17:00</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} The Indian Public School, Bengaluru. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <Link to="/about" className="hover:text-[#C83C4A] transition-colors">Privacy Policy</Link>
            <span>·</span>
            <Link to="/about" className="hover:text-[#C83C4A] transition-colors">Terms of Use</Link>
            <span>·</span>
            <Link to="/contact" className="hover:text-[#C83C4A] transition-colors">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;